import {
  useQuery,
  useMutation,
  type UseQueryOptions,
  type UseMutationOptions,
  type UseQueryResult,
  type UseMutationResult,
} from "@tanstack/react-query";
import type { ApiClient } from "../client";
import type { ApiError } from "../errors/ApiError";

/**
 * Generic factory hooks for entity-specific hook modules. Each Phase (1..5) uses
 * these to produce tenant-scoped hooks like `useEmployeeList`. Example:
 *
 *   export function useEmployeeList(slug: string, query: EmployeeQuery) {
 *     const client = useApiClient();
 *     return useTenantQuery(client, {
 *       queryKey: qk.employees.list(slug, query),
 *       queryFn: (c) => c.GET("/api/v1/employees", { params: { query } }).then(r => r.data!),
 *     });
 *   }
 *
 * Keeping the factory thin (a type-safe wrapper over `useQuery`/`useMutation`)
 * means every feature team can write their hook in ≤10 lines with full type
 * inference. Errors are typed as `ApiError` — pair with `ts-pattern`:
 *
 *   const { data, error } = useEmployeeList(slug, query);
 *   if (error) match(error).with({ errorCode: "FORBIDDEN" }, ...).exhaustive();
 *
 * Names start with `use` because these are hooks (call `useQuery` /
 * `useMutation` internally) — required for React's rules-of-hooks lint.
 * Aliases `makeTenantQuery` / `makeTenantMutation` are re-exported for callers
 * that prefer factory-style naming in their source.
 */

export type ApiQueryOptions<TData> = Omit<UseQueryOptions<TData, ApiError>, "queryFn"> & {
  queryFn: (client: ApiClient) => Promise<TData>;
};

export function useTenantQuery<TData>(
  client: ApiClient,
  opts: ApiQueryOptions<TData>,
): UseQueryResult<TData, ApiError> {
  const { queryFn, ...rest } = opts;
  return useQuery<TData, ApiError>({
    ...rest,
    queryFn: () => queryFn(client),
  });
}

export type ApiMutationOptions<TVars, TData, TContext = unknown> = Omit<
  UseMutationOptions<TData, ApiError, TVars, TContext>,
  "mutationFn"
> & {
  mutationFn: (client: ApiClient, vars: TVars) => Promise<TData>;
};

export function useTenantMutation<TVars, TData, TContext = unknown>(
  client: ApiClient,
  opts: ApiMutationOptions<TVars, TData, TContext>,
): UseMutationResult<TData, ApiError, TVars, TContext> {
  const { mutationFn, ...rest } = opts;
  return useMutation<TData, ApiError, TVars, TContext>({
    ...rest,
    mutationFn: (vars) => mutationFn(client, vars),
  });
}

/** Factory-style aliases — same function, same semantics. */
export const makeTenantQuery = useTenantQuery;
export const makeTenantMutation = useTenantMutation;
